// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      if (token && storedUser) {
        setUser(JSON.parse(storedUser));
        const response = await authAPI.getMe();
        if (response?.success) {
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } else {
          logout();
        }
      }
    } catch (err) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.login({ email, password });
      if (response?.success) {
        const { token, user: userData } = response;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        return { success: true, user: userData };
      }
      throw new Error(response.message || 'Login failed');
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // NEW: ADDED REGISTER COMPANY
  const registerCompany = async (companyData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.registerCompany(companyData);
      if (response?.success) {
        // Many systems auto-login after register. 
        // If your backend returns token/user, handle it here:
        if (response.token) {
          localStorage.setItem('token', response.token);
          localStorage.setItem('user', JSON.stringify(response.user));
          setUser(response.user);
        }
        return { success: true };
      }
      throw new Error(response.message || 'Registration failed');
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // NEW: ADDED REGISTER DRIVER
  const registerDriver = async (driverData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.registerDriver(driverData);
      if (response?.success) {
        if (response.token) {
          localStorage.setItem('token', response.token);
          localStorage.setItem('user', JSON.stringify(response.user));
          setUser(response.user);
        }
        return { success: true };
      }
      throw new Error(response.message || 'Registration failed');
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setError(null);
    window.location.href = '/login';
  };

  const value = { 
    user, 
    loading, 
    error, 
    login, 
    logout, 
    registerCompany, // Added to value
    registerDriver,  // Added to value
    setUser, 
    checkAuthStatus 
  };
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};